import React, { useEffect, useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import "./login.css";
import { Link } from "react-router-dom";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { errorLog } from "../../utils/logger";
import { GoogleSignup, Tokens } from "../../models/Interfaces";
import { useTranslation, Trans } from "react-i18next";
import { Login as LoginInput } from "../../models/Interfaces";
import { Squirrel } from "../../components/spinner/Squirrel";
import {
  getAuth,
  connectAuthEmulator,
  signInWithPopup,
  GoogleAuthProvider,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { initializeApp } from "firebase/app";
import { firebaseConfig } from "./firebaseConfig";
import { REDIRECT_URI, STAGE, STAY_LOGGED_IN_PW, WHITELABEL_KEY } from "../../globals";
import ResponsiveLayoutCard from "../../components/onboarding/ResponsiveLayoutCard/ResponsiveLayoutCard";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import { useBranding } from "../../branding/useBranding";
import {
  appSettingsAtom,
  loadableBiometricAuthAtom,
  highContrastAtom,
  writeOnlyTokensAtom,
  writeOnlyUserAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import db from "../../database/database";
import { UserResponseDto } from "@docket/consumer-app-client-typescript-axios";
import useFormFactor from "../../hooks/useFormFactor";
import { DocketSwitch } from "../../components/form/DocketSwitch";
import { isAuth, updateCryptoKeyAndUserAndTokens } from "../../utils/auth";
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { Capacitor } from "@capacitor/core";
import AppleSignIn from "./AppleSignIn";
import { vibrateDevice } from "../../utils/capacitor";

const app = initializeApp(firebaseConfig());
const provider = new GoogleAuthProvider();

export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const branding = useBranding();
  const { formFactor } = useFormFactor();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoginWithState, setIsLoginWithState] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [openEmailPrompt, setOpenEmailPrompt] = useState(false);
  const [promptedEmail, setPromptedEmail] = useState("");
  const [_nullUser, setUser] = useAtom(writeOnlyUserAtom);
  const [_nullTokens, setTokens] = useAtom(writeOnlyTokensAtom);
  const [appSettings, setAppSettings] = useAtom(appSettingsAtom);
  const [highContrast, setHighContrast] = useAtom(highContrastAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [biometricAuth] = useAtom(loadableBiometricAuthAtom);
  const [biometryAvailable, setBiometryAvailable] = useState<boolean | null>(null);

  const errorMessage = useRef(t("onboarding.login_error"));

  const auth = getAuth(app);
  if (STAGE === "development" && !appSettings.firebaseEmulatorConnected) {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    setAppSettings({
      ...appSettings,
      firebaseEmulatorConnected: true,
    });
  }

  const loginWithPromptedEmail = () => {
    setOpenEmailPrompt(false);
    setLoggingIn(true);
    signInWithEmailLink(auth, promptedEmail, window.location.href)
      .then((result) => {
        result.user.getIdToken().then((idToken) => {
          getAPIClient()
            .loginWithFirebase(idToken)
            .then(async (loginResult) => {
              if (!loginResult || !loginResult.tokens) {
                // This really shouldn't happen
                const err = new Error("Did not get a log in result or token");
                Bugsnag.notify(err);
                throw err;
              }

              await setAppSettings({
                ...appSettings,
                email: loginResult.email || "",
                signedInWithFirebase: true,
              });

              // This does magic to ensure the user is actually stored locally
              await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);

              // First param is the user ID, which we don't expose
              Bugsnag.setUser("", loginResult.email);
              // Check if user doesn't have info fill out
              if (
                !loginResult.dob ||
                !loginResult.legal_sex ||
                !loginResult.first_name ||
                !loginResult.last_name
              ) {
                navigate("/signup/accountinfo");
              } else {
                navigate("/home/search");
              }
            })
            .catch((e: any) => {
              if (e instanceof DocketAPIError) {
                errorMessage.current = e.message;
              } else {
                errorLog(e);
              }
              setShowErrorModal(true);
            });
        });
      })
      .catch((error) => {
        errorMessage.current = t("onboarding.login-error");
        setShowErrorModal(true);
      })
      .finally(() => {
        window.localStorage.removeItem("emailForSignIn");
        setLoggingIn(false);
      });
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      BiometricAuth.checkBiometry().then((value) => {
        setBiometryAvailable(value.isAvailable);
      });
    }
  }, []);

  useEffect(() => {
    // Check for 'code' query param in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const stateParameter = urlParams.get("stateIdentifier");
    const code = urlParams.get("code");

    /* Magical links */
    const auth = getAuth(app);
    if (STAGE === "development" && !appSettings.firebaseEmulatorConnected) {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
      setAppSettings({
        ...appSettings,
        firebaseEmulatorConnected: true,
      });
    }

    if (isSignInWithEmailLink(auth, window.location.href)) {
      setLoggingIn(true);
      const email = window.localStorage.getItem("emailForSignIn");

      if (email) {
        FirebaseAuthentication.signInWithEmailLink({
          email,
          emailLink: window.location.href,
        })
          .then((result) => {
            FirebaseAuthentication.getIdToken().then((idToken) => {
              getAPIClient()
                .loginWithFirebase(idToken.token)
                .then(async (loginResult) => {
                  if (!loginResult || !loginResult.tokens) {
                    // This really shouldn't happen
                    const err = new Error("Did not get a log in result or token");
                    Bugsnag.notify(err);
                    throw err;
                  }

                  await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
                  await setAppSettings({
                    ...appSettings,
                    email: loginResult.email || "",
                  });

                  //dispatch(setPassword(STAY_LOGGED_IN_PW));
                  //await db().setItem(Key.UserAccount, userWithPassword);
                  // First param is the user ID, which we don't expose
                  Bugsnag.setUser("", loginResult.email);
                  // Check if user doesn't have info fill out
                  if (
                    !loginResult.dob ||
                    !loginResult.legal_sex ||
                    !loginResult.first_name ||
                    !loginResult.last_name
                  ) {
                    navigate("/signup/accountinfo");
                  } else {
                    navigate("/home/search");
                  }
                })
                .catch((e: any) => {
                  if (e instanceof DocketAPIError) {
                    errorMessage.current = e.message;
                  } else {
                    errorLog(e);
                  }
                  setShowErrorModal(true);
                });
            });
          })
          .catch((error) => {
            errorMessage.current = t("onboarding.login-error");
            setShowErrorModal(true);
          })
          .finally(() => {
            window.localStorage.removeItem("emailForSignIn");
            setLoggingIn(false);
          });
      } else {
        setLoggingIn(false);
        setOpenEmailPrompt(true);
      }
    }

    /* State OIDC */
    if (code && stateParameter) {
      setLoggingIn(true);
      getAPIClient()
        .loginWithAuthCode(code, stateParameter)
        .then(async (loginResult) => {
          if (!loginResult || !loginResult.tokens) {
            // This really shouldn't happen
            const err = new Error("Did not get a log in result or token");
            Bugsnag.notify(err);
            throw err;
          }

          await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
          await setAppSettings({
            ...appSettings,
            email: loginResult.email || "",
          });
          // First param is the user ID, which we don't expose
          Bugsnag.setUser("", loginResult.email);
          // Check if user doesn't have info fill out
          if (
            !loginResult.dob ||
            !loginResult.legal_sex ||
            !loginResult.first_name ||
            !loginResult.last_name
          ) {
            navigate("/signup/accountinfo");
          } else {
            navigate("/home/search");
          }
          setLoggingIn(false);
        })
        .catch((e: any) => {
          if (e instanceof DocketAPIError) {
            errorMessage.current = e.message;
          } else {
            errorLog(e);
          }
          setShowErrorModal(true);
          setLoggingIn(false);
        });
    }
  }, []);

  useEffect(() => {
    // Check if the user is already logged in
    const checkAuth = async () => {
      if (biometricAuth.state !== "hasData") return;
      // Want to ensure that device biometry support finished loading
      if (Capacitor.isNativePlatform() && biometryAvailable === null) return;

      const authed = await isAuth();
      if (!authed) return;

      // Do not want to navigate if biometrics is unavailable, despite setting turned on
      if (biometricAuth.data && biometryAvailable) {
        navigate("/biometrics");
      } else {
        navigate("/home/records", { replace: true });
      }
    };
    checkAuth();
  }, [biometricAuth, biometryAvailable]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginInput>();
  const onMagicLink: SubmitHandler<LoginInput> = async (data) => {
    try {
      setIsSubmitting(true);
      window.localStorage.setItem("emailForSignIn", data.email);
      await getAPIClient().sendSignInLink(data.email);
      setMagicLinkSent(true);
    } catch (e: any) {
      if (e && e instanceof DocketAPIError) {
        errorMessage.current = e.message;
        if ((e.cause as any)?.response?.status === 429) {
          errorMessage.current = t("onboarding.login-error-429");
        }
        errorLog(e);
      } else {
        errorLog(e);
      }
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const signInWithGoogleWeb = async () => {
    const result = await signInWithPopup(auth, provider);

    if (!result) {
      errorMessage.current = t("onboarding.login-error-google");
      setShowErrorModal(true);
      return;
    }

    const { user: googleuser } = result;
    const credential = GoogleAuthProvider.credentialFromResult(result);

    const googleSignup: GoogleSignup = {
      email: googleuser?.email || "",
      first_name: googleuser?.displayName?.split(" ")[0] || "",
      last_name: googleuser?.displayName?.split(" ")[1] || "",
      token: credential?.idToken || "",
      google_id: googleuser?.uid,
      offset: "", //NSTimeZone.system.secondsFromGMT() equivalent
    };

    return googleSignup;
  };

  const signInWithGoogleNative = async () => {
    const { credential, user } = await FirebaseAuthentication.signInWithGoogle();

    if (!credential?.idToken) {
      errorMessage.current = t("onboarding.login-error-google");
      setShowErrorModal(true);
      return;
    }

    const googleCredential = GoogleAuthProvider.credential(credential.idToken);

    const googleSignup: GoogleSignup = {
      email: user?.email || "",
      first_name: user?.displayName?.split(" ")[0] || "",
      last_name: user?.displayName?.split(" ")[1] || "",
      token: googleCredential?.idToken || "",
      google_id: user?.uid || "",
      offset: "", //NSTimeZone.system.secondsFromGMT() equivalent
    };

    return googleSignup;
  };

  const signInWithGoogle = async () => {
    const googleSignup = Capacitor.isNativePlatform()
      ? await signInWithGoogleNative()
      : await signInWithGoogleWeb();

    if (!googleSignup) return;

    try {
      const loginResult = await getAPIClient().loginGoogle(googleSignup);

      if (!loginResult || !loginResult.tokens?.access) {
        // This really shouldn't happen
        const err = new Error("Did not get a login result or token");
        Bugsnag.notify(err);
        throw err;
      }

      await updateCryptoKeyAndUserAndTokens(loginResult, loginResult.tokens);
      await setAppSettings({
        ...appSettings,
        email: loginResult.email || "",
      });

      // First param is the user ID, which we don't expose
      Bugsnag.setUser("", loginResult.email);
      if (
        !loginResult.dob ||
        !loginResult.legal_sex ||
        !loginResult.first_name ||
        !loginResult.last_name
      ) {
        navigate("/signup/accountinfo");
      } else {
        navigate("/home/search");
      }
    } catch (e: any) {
      if (e instanceof DocketAPIError) {
        errorMessage.current = e.message;
      } else {
        errorLog(e);
      }
      setShowErrorModal(true);
    }
  };

  const loginWithState = async (state: string) => {
    await getAPIClient().initiateOidcAuth(state);
  };

  const handleAppleSignInError = (error: string) => {
    errorMessage.current = error;
    setShowErrorModal(true);
  };

  const toggleHighContrast = async (enabled: boolean) => {
    if (enabled) await vibrateDevice();
    setHighContrast(enabled);
  };

  return (
    <section
      className={`md:p-12 max-md:overflow-hidden h-screen max-md:flex max-md:flex-col dark:bg-dark ${
        magicLinkSent ? "max-md:fixed" : ""
      }`}
    >
      {magicLinkSent && (
        <div className="md:pb-8 max-md:hidden">
          <Link
            to="/"
            className={`is-${branding.backlinkColor}-text`}
            onClick={() => setMagicLinkSent(false)}
          >
            {t("onboarding.signup-back-button")}
          </Link>
        </div>
      )}
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>
          <p>{errorMessage.current}</p>
        </ErrorModal>
      )}
      <ResponsiveLayoutCard>
        <div className="flex">
          <div
            className={`flex flex-1 flex-col p-9 gap-4 ${
              magicLinkSent ? "max-md:fixed h-full" : ""
            }`}
          >
            {magicLinkSent && (
              <div className="flex">
                <Link
                  to="/"
                  className={`is-${branding.backlinkColor}-text max-md:py-auto max-md:w-1/5 md:hidden`}
                  onClick={() => setMagicLinkSent(false)}
                >
                  {t("onboarding.signup-back-button")}
                </Link>
                <p className="md:hidden mobile-text-color text-xl w-3/5 text-center font-medium">
                  {t("onboarding.signup_or_login")}
                </p>
                <div className="w-1/5"></div>
              </div>
            )}
            {!magicLinkSent && (
              <img
                src={
                  highContrast ? branding.mobileHeaderImageHighContrast : branding.mobileHeaderImage
                }
                alt="Docket logo"
                className="m-auto max-md:w-48 md:hidden"
                data-testid="docket-logo"
              />
            )}
            <img
              src={branding.logo}
              alt="Docket logo"
              className="max-md:hidden md:inline"
              data-testid="docket-logo"
            />
            <div className="separator max-md:hidden"></div>
            {isLoginWithState ? (
              <>
                <a
                  onClick={() => setIsLoginWithState(false)}
                  role="button"
                  className="docket-link text-base font-normal mobile-text-color cursor-pointer"
                >
                  {t("generic.back")}
                </a>
                <div className="mobile-text-color text-s">{t("onboarding.state-disclaimer")}</div>
                <button
                  data-testid="alaska-login-button"
                  onClick={() => loginWithState("ak")}
                  type="button"
                  className="!btn !bg-white !h-16 !p-4"
                >
                  <img src={"assets/images/myalaska.png"} className="state-id-img" />
                </button>
                <button
                  data-testid="utah-login-button"
                  onClick={() => loginWithState("ut")}
                  type="button"
                  className="!btn !bg-white !h-16 !p-4"
                >
                  <img src={"assets/images/utahid-logo.svg"} className="state-id-img" />
                </button>
                <div className="mt-16">
                  <div className="mobile-text-color text-xs">
                    <b>{t("generic.warning")}:</b> {t("onboarding.state-persist-message")}
                  </div>
                </div>
                <p className="mobile-text-color text-xs">
                  <Trans i18nKey="onboarding.inactivity-message" />
                </p>
              </>
            ) : (
              <>
                {magicLinkSent && (
                  <div className="flex flex-col gap-4 text-center h-full">
                    <div
                      data-testid="sent-signin-link"
                      className="text-dark mobile-text-color text-2xl font-medium max-md:text-xl"
                    >
                      {t("onboarding.sent-signin-link")}
                    </div>
                    <p className="text-dark mobile-text-color email-wrap max-md:pt-6 text-sm">
                      {window.localStorage.getItem("emailForSignIn")}
                    </p>
                    <p className="text-dark mobile-text-color font-medium max-md:hidden">
                      {t("onboarding.click-signin-link")}
                    </p>
                    <p className="text-dark mobile-text-color font-medium md:hidden">
                      Tap the sign-in link <i className="font-semibold">using this device</i> to
                      confirm your email address.
                    </p>
                    <img
                      src={"assets/images/Icon_Info.png"}
                      className="md:hidden size-[70px] mx-auto mt-16 dark:hidden"
                    />
                    <p className="dark:hidden text-dark mobile-text-color font-medium md:hidden text-md pt-6">
                      {t("onboarding.enable-notification-reminders")}
                    </p>
                    <div className="hidden bg-docket-primary rounded-xl mt-12 h-full max-md:dark:flex flex-col">
                      <img
                        src={"assets/images/Icon_Info.png"}
                        className="md:hidden size-[70px] mx-auto mt-4"
                      />
                      <p className="text-dark mobile-text-color font-medium md:hidden text-md pt-6">
                        {t("onboarding.enable-notification-reminders")}
                      </p>
                      <img
                        src="assets/images/Virus_Light.png"
                        className="inline dark:hidden md:hidden mt-auto"
                      />
                      <img
                        src="assets/images/Virus_HC.png"
                        className="hidden dark:inline md:hidden mt-auto"
                      />
                    </div>
                  </div>
                )}
                {loggingIn && (
                  <div className="is-fullWidth mt-6">
                    <Squirrel />
                  </div>
                )}
                {!magicLinkSent && !loggingIn && (
                  <>
                    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onMagicLink)}>
                      <fieldset className="fieldset py-0">
                        <legend className="fieldset-legend form-input-label max-md:hidden">
                          {t("onboarding.email-address")}
                        </legend>
                        <input
                          data-testid="email-input"
                          type="email"
                          className="input input-responsive w-full text-black mobile-text-color max-md:text-lg max-md:!rounded-md"
                          aria-label={t("onboarding.email-address")}
                          placeholder={t("onboarding.email-address")}
                          defaultValue=""
                          {...register("email", {
                            required: t("onboarding.email-required"),
                          })}
                        />
                        {errors["email"] && (
                          <div className="validation-error">
                            {<span>{errors["email"] && errors["email"].message}</span>}
                          </div>
                        )}
                      </fieldset>
                      <p className="max-md:hidden has-text-dark text-xs font-normal">
                        <Trans i18nKey="onboarding.inactivity-message" />
                      </p>
                      <button
                        data-testid="login-btn"
                        className="btn text-s docket-button-responsive max-md:text-lg"
                        type="submit"
                        disabled={isSubmitting || errors.email !== undefined}
                      >
                        {t("onboarding.signup_or_login")}
                      </button>
                    </form>
                    <div className="flex">
                      <hr className="separator flex-3 mobile-separator"></hr>
                      <div className="flex md:pr-2.5 md:pl-2 md:py-2 max-md:py-auto text-center mobile-circle text-black">
                        <p className="my-auto ml-[9px]">
                          {formFactor === "desktop" ? t("onboarding.or-login-with") : "OR"}
                        </p>
                      </div>
                      <hr className="separator flex-3 mobile-separator"></hr>
                    </div>
                    <div className="flex gap-4 max-md:flex-col">
                      <button
                        data-testid="Sign-in-Google"
                        onClick={signInWithGoogle}
                        type="button"
                        className="!btn !flex-1 !bg-white !px-3 !py-8 max-md:!px-1 max-md:!py-2 max-md:!border-none"
                      >
                        <figure className="image w-[30px] mr-0 md:w-[40px] md:mr-[10px]">
                          <img src="/assets/images/google_button.png" alt="Sign in with Google" />
                        </figure>
                        <span className="text-2xl font-normal max-md:hidden has-text-dark">
                          Google
                        </span>
                      </button>
                      <AppleSignIn onError={handleAppleSignInError} />
                    </div>
                    <button
                      data-testid="state-issued-button"
                      onClick={() => setIsLoginWithState(true)}
                      type="button"
                      className="btn !text-xl !font-normal !bg-white !text-black !px-3 !py-8 max-md:!hidden"
                    >
                      {t("onboarding.state-login-option")}
                    </button>
                    <a
                      className="md:hidden mx-auto mobile-text-color cursor-pointer underline"
                      onClick={() => setIsLoginWithState(true)}
                    >
                      {t("onboarding.state-login-option")}
                    </a>
                    <div className="md:hidden flex gap-1 mx-auto mt-6 high-contrast-visibility">
                      <p className="text-lg font-semibold">High-contrast Mode</p>
                      <DocketSwitch
                        checked={highContrast}
                        onChange={(e) => toggleHighContrast(e.target.checked)}
                        id={"high-contrast"}
                        name={"high-contrast"}
                      />
                    </div>
                    <div className="has-text-dark text-xs max-md:hidden">
                      {t("onboarding.allow-popups")}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="md:flex-1 signup-image max-md:hidden rounded-tr-[22px] rounded-br-[22px]">
            <img className="max-md:hidden" src={branding.loginBackgroundImage} />
            <div className="login-badge max-md:hidden">{t("onboarding.immunization_slogan")}</div>
          </div>
        </div>
      </ResponsiveLayoutCard>
      {magicLinkSent && (
        <img
          src={`assets/images/Virus_${highContrast ? "HC" : "Light"}.png`}
          className="md:hidden w-full mt-auto dark:hidden"
        />
      )}
      <Modal
        id="email-prompt"
        open={openEmailPrompt}
        onCloseModal={() => setOpenEmailPrompt(false)}
        hasClose={true}
        title={t("onboarding.enter_email")}
        onConfirm={loginWithPromptedEmail}
        confirm={t("onboarding.login")}
      >
        <div className="field mb-4 mt-4">
          <p className="mb-4">{t("onboarding.login-reenter-email")}</p>
          <fieldset className="fieldset p-0">
            <legend className="fieldset-legend pt-0 text-[16px] font-[700] text-black">
              Email
            </legend>
            <input
              id="promptedEmail"
              className="input w-full text-black"
              placeholder={t("onboarding.email")}
              value={promptedEmail}
              onChange={(e) => setPromptedEmail(e.target.value)}
            />
          </fieldset>
        </div>
      </Modal>
      {WHITELABEL_KEY === "nj" && (
        <footer className="footer mt-5 pb-0 max-md:hidden">
          <div className="footer-content mx-auto">
            <div className="is-flex-tablet is-justify-content-center is-align-items-center">
              <a
                className="link mr-2 ml-2 py-1 is-block has-text-centered"
                href="https://www.nj.gov/health/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.nj_link")}
              </a>
              <span className="has-text-dark is-hidden-mobile py-1">|</span>
              <a
                className="link mr-2 ml-2 py-1 is-block has-text-centered"
                href="https://dockethealth.com/terms_of_use"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.terms_link")}
              </a>
              <span className="has-text-dark is-hidden-mobile py-1">|</span>
              <a
                className="link mr-2 ml-2 py-1 is-block has-text-centered"
                href="https://dockethealth.com/privacy_policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("onboarding.privacy_link")}
              </a>
            </div>
          </div>
        </footer>
      )}
    </section>
  );
}

export default Login;
